<template>
  <v-expand-transition>
    <v-col cols="12">
      <v-card v-bind="$attrs">
        <v-card-title>
          <section-header v-if="hasNotCreatedTasks"
                          icon="mdi mdi-format-list-checks">
            {{ $t('surveys.generate_tasks') }}
          </section-header>
          <section-header v-else icon="mdi mdi-format-list-checks">
            {{ $t('surveys.generated_tasks') }}
          </section-header>
          <v-spacer></v-spacer>
        </v-card-title>

        <v-card-text v-if="firstLoad">
          <v-skeleton-loader type="list-item-two-line@3"></v-skeleton-loader>
        </v-card-text>

        <template v-if="!firstLoad">
          <v-card-text v-if="hasNotCreatedTasks"
                       class="text-body-2 text-sm-body-1">
            {{ $t('surveys.generate_tasks_help') }}
          </v-card-text>
          <v-card-subtitle v-if="hasNotCreatedTasks"
                           class="font-weight-bold">
            {{ $t('surveys.tasks_not_created') }}:
          </v-card-subtitle>
          <v-card-text v-if="hasNotCreatedTasks">
            <v-row>
              <v-col cols="12">
                <div class="tasks-ul">
                  <template v-for="task in filteredSuggestedTasksNotCreated">
                    <v-row :key="task.key">
                      <v-col class="d-flex tasks-li align-center" cols="12">
                        <div class="mr-2">
                          <v-icon>mdi mdi-circle-small</v-icon>
                        </div>
                        <div>
                          {{ task.nameText }}
                        </div>
                      </v-col>
                    </v-row>
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-subtitle v-if="filteredSuggestedTasksCreated && filteredSuggestedTasksCreated.length > 0"
                           class="font-weight-bold mt-5">
            {{ $t('surveys.tasks_created') }}:
          </v-card-subtitle>
          <v-card-text v-if="filteredSuggestedTasksCreated && filteredSuggestedTasksCreated.length > 0">
            <v-row>
              <v-col cols="12">
                <div class="tasks-ul">
                  <template v-for="task in filteredSuggestedTasksCreated">
                    <v-row :key="task.key">
                      <v-col class="d-flex tasks-li align-center" cols="12">
                        <div class="mr-2">
                          <v-icon>mdi mdi-check</v-icon>
                        </div>
                        <div>
                          {{ task.nameText }}
                        </div>
                      </v-col>
                    </v-row>
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <basic-button v-if="hasNotCreatedTasks"
                          :block="$vuetify.breakpoint.xsOnly"
                          :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                          :disabled="loading || !$hasMatchingTenantRoles(['tenant_admin'])"
                          :loading="loadingTaskSave"
                          :outlined="$vuetify.breakpoint.xsOnly"
                          color="primary"
                          text
                          @click="saveSuggestedTasks">
              <v-icon left>add</v-icon>
              {{ $t('surveys.generate_tasks') }}
            </basic-button>
            <basic-button v-if="filteredSuggestedTasksCreated && filteredSuggestedTasksCreated.length > 0"
                          :block="$vuetify.breakpoint.xsOnly"
                          :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                          :outlined="$vuetify.breakpoint.xsOnly"
                          color="primary"
                          text
                          @click="goToTasks">
              <v-icon v-if="$icons" left>mdi mdi mdi-arrow-right</v-icon>
              {{ $t('dashboard.open_tasks') }}
            </basic-button>
          </v-card-actions>
        </template>
      </v-card>
    </v-col>
  </v-expand-transition>
</template>

<script>
import taskApi from "@/api/task";
import staticTaskApi from "@/api/statictask";
import SectionHeader from "@/components/SectionHeader";
import BasicButton from "@/components/BasicButton";
import pLimit from 'p-limit';
import dayjs from "dayjs";

export default {
  name: "SurveyInterpretationTasksCard",
  components: {BasicButton, SectionHeader},
  props: ['survey', 'loading', 'automaticallyCreateTasks'],
  data() {
    return {
      firstLoad: false,
      loadingTaskSave: false,
      suggestedTasks: [],
      tasks: [],
    }
  },
  methods: {
    async reloadTasks() {
      try {
        const tasks = await taskApi.getTasks();
        this.tasks = tasks;
      } catch (error) {
        this.$handleApiError(error, this.$t('errors.error_loading_tasks'));
      }
    },
    async reloadSuggestedTasks() {
      try {
        let tasks = await staticTaskApi.getStaticTasks();
        tasks = tasks.map(task => {
          task.searchString = `${task.nameText} ${task.descriptionText}`;
          return task;
        });
        this.suggestedTasks = tasks;
      } catch (e) {
        console.error(e);
        //this.$handleApiError(e, this.$t('errors.error_loading_tasks'));
      }
    },
    async saveSuggestedTasks() {

      try {
        const limit = pLimit(3);
        const promises = [];
        const selectedSuggestedTasks = this.filteredSuggestedTasksNotCreated
        if(selectedSuggestedTasks.length === 0) {
          return;
        }
        this.loadingTaskSave = true;
        for (let stask of selectedSuggestedTasks) {
          promises.push(limit(async () => {
            try {
              const t = {
                staticTaskId: stask.id,
                type: stask.type,
                name: stask.nameText,
                description: stask.descriptionText,
                priority: stask.priority,
                category: null
              }
              if (this.survey.end) {
                t.dueDate = dayjs(this.survey.end).add(7, 'days').format('YYYY-MM-DD');
              }
              await taskApi.createTask(t)
            } catch (e) {
              this.$handleApiError(e, this.$t('errors.error_saving_task'));
            }
          }))
        }
        await Promise.all(promises);

        if(!this.automaticallyCreateTasks) {
          this.$showSuccessNotification(this.$t('tasks.suggested_tasks_saved'));
        }
      } catch (e) {
        console.error(e)
        this.$handleApiError(e, this.$t('errors.error_saving_task'));
      }
      await this.reloadTasks().then(async () => await this.reloadSuggestedTasks());
      this.loadingTaskSave = false;
      // save selected tasks
    },
    goToTasks() {
      this.$router.push({name: 'tenant_tasks'});
    }
  },
  computed: {
    hasNotCreatedTasks() {
      return this.filteredSuggestedTasksNotCreated && this.filteredSuggestedTasksNotCreated.length > 0
    },
    filteredSuggestedTasksNotCreated() {
      return this.suggestedTasks.filter(task => {
        return task?.type === this.survey?.surveyTemplate?.type?.toLocaleLowerCase() + '_help';
      }).filter(task => {
        return !this.tasks.find(t => t.staticTaskId === task.id && t.status !== 'DONE');
      });
    },
    filteredSuggestedTasksCreated() {
      return this.suggestedTasks.filter(task => {
        return task?.type === this.survey?.surveyTemplate?.type?.toLocaleLowerCase() + '_help';
      }).filter(task => {
        return this.tasks.find(t => t.staticTaskId === task.id && t.status !== 'DONE');
      });
    },
  },
  async mounted() {
    this.firstLoad = true;
    await this.reloadTasks();
    await this.reloadSuggestedTasks();
    if(this.automaticallyCreateTasks) {
      await this.saveSuggestedTasks();
    }
    this.firstLoad = false;
  }
}
</script>

<style scoped>

.tasks-li {

}
</style>
